// src/services/autocompleteService.tsx
import axios from 'axios';
import { buildApiUrl } from '../config/apiConfig';

export type AutocompleteType = 'profession' | 'service' | 'pro' | 'association';

export interface AutocompleteItem {
  type: AutocompleteType;
  id: number;
  name: string;
  slug?: string | null;
  icon?: string | null;
}

/**
 * fetchAutocomplete
 * Effectue un appel GET vers l'endpoint Chalice "/autocomplete?query=..."
 * et récupère la liste des objets {type, id, name, slug}.
 * Les résultats `service` représentent des types de services canoniques (service_types),
 * pas des prestations individuelles d'un pro.
 * Utilise le cache pour améliorer les performances.
 */
export async function fetchAutocomplete(query: string): Promise<AutocompleteItem[]> {
  if (!query) return [];

  // Vérifier le cache d'abord (cache simplifié intégré)
  try {
    const cached = localStorage.getItem(`autocomplete_cache_${query.toLowerCase()}`);
    if (cached) {
      const data = JSON.parse(cached);
      if (Date.now() - data.timestamp < 300000) {
        // 5 minutes
        // Incrémenter le compteur de cache hits
        const cacheHits = parseInt(localStorage.getItem('search_cache_hits') || '0') + 1;
        localStorage.setItem('search_cache_hits', cacheHits.toString());
        return data.results;
      }
    }
  } catch (error) {
    // Ignore cache errors
  }

  // Incrémenter le compteur de recherches totales
  const totalSearches = parseInt(localStorage.getItem('search_total_searches') || '0') + 1;
  localStorage.setItem('search_total_searches', totalSearches.toString());

  try {
    // On utilise axios en GET, avec `params` pour query=...
    const response = await axios.get(buildApiUrl('/autocomplete'), {
      params: { query },
    });

    // Pour les résultats de type "pro", on ajoute l'icône
    let results: AutocompleteItem[] = response.data.results || [];

    if (results.length > 0) {
      // Ne pas faire échouer tout l'autocomplete si une photo pro échoue (Promise.all).
      results = await Promise.all(
        results.map(async (item: AutocompleteItem) => {
          if (item.type === 'pro' && item.slug) {
            try {
              const icon = await fetchProPicture(item.slug);
              return { ...item, icon };
            } catch {
              return { ...item, icon: undefined };
            }
          }
          return item;
        })
      );
    }

    // Ne pas mettre en cache les réponses vides : évite de figer un « pas de pro »
    // après une erreur transitoire ou un correctif API (ex. « Jean Treins » vs « Jean Trei »).
    if (results.length > 0) {
      try {
        const data = { results, timestamp: Date.now() };
        localStorage.setItem(`autocomplete_cache_${query.toLowerCase()}`, JSON.stringify(data));
      } catch (error) {
        // Ignore cache errors
      }
    }

    return results;
  } catch (error) {
    // TODO: Remplacer par un système de logging approprié
    // console.error("Erreur lors de l'auto-complétion:", error);
    return [];
  }
}

export async function fetchProPicture(slug: string): Promise<string> {
  const pro = await axios.get(buildApiUrl(`/get-pro-by-slug/${slug}`));
  return pro.data.profile_photo_url;
}
